import { useMemo } from 'react';
import type { InputDropdownOption } from '@interxfi/design-system/components/input/shared/dropdown/input-dropdown';
import { type I18nNamespaces, useTranslation } from '@interxfi/translation/generated';

type FixedOptionInputFilter<TValue extends string = string> = (value: TValue) => boolean;

export interface FixedOptionInputBaseProps<TValue extends string = string> {
  filter?: FixedOptionInputFilter<TValue>;
  i18nPrefix: 'oneOf' | 'enum' | 'polymorph';
  i18nValueName: string;
  i18nNamespace: I18nNamespaces;
  optionModifier?: (options: InputDropdownOption<TValue>[]) => InputDropdownOption<TValue>[];
  optionValues: TValue[] | readonly TValue[];
  sortByLabel?: boolean | ((a: TValue, b: TValue) => number);
}

export function useFixedOptionInputOptions<TValue extends string = string>({
  filter,
  i18nNamespace,
  i18nPrefix,
  i18nValueName,
  optionModifier,
  optionValues,
  sortByLabel = true,
}: FixedOptionInputBaseProps<TValue>) {
  const { t } = useTranslation(i18nNamespace);

  return useMemo(() => {
    const base = (optionValues as TValue[]).reduce<InputDropdownOption<TValue>[]>((acc, curr) => {
      if (filter && !filter(curr)) {
        return acc;
      }

      acc.push({
        label: t(`${i18nNamespace}:${i18nPrefix}.${i18nValueName}.${curr}`, { defaultValue: curr }),
        value: curr,
      });

      return acc;
    }, []);

    if (sortByLabel) {
      base.sort((a, b) => {
        if (typeof sortByLabel === 'function') {
          return sortByLabel(a.value, b.value);
        }

        return a.label.localeCompare(b.label);
      });
    }

    return optionModifier?.(base) ?? base;
  }, [t, i18nNamespace, i18nPrefix, i18nValueName, optionValues, optionModifier, filter, sortByLabel]);
}
